import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { adminAPI } from '../../services/api';
import { MENU_CATEGORIES, CATEGORY_LABELS } from '../../utils/constants';

function MenuItemsTab({ message, setMessage }) {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    category: 'HOT',
    itemName: '',
    description: '',
    itemPlu: '',
    modifierPlu: '',
    displayOrder: '',
    active: true
  });

  useEffect(() => {
    loadMenuItems();
  }, []);

  const loadMenuItems = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllMenuItems();
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to load menu items:', error);
      setMessage({ type: 'danger', text: 'Failed to load menu items' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingItem(null);
    setFormData({
      category: 'HOT',
      itemName: '',
      description: '',
      itemPlu: '',
      modifierPlu: '',
      displayOrder: getNextDisplayOrder('HOT'),
      active: true
    });
    setShowModal(true);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      itemName: item.item_name,
      description: item.description || '',
      itemPlu: item.item_plu || '',
      modifierPlu: item.modifier_plu || '',
      displayOrder: item.display_order,
      active: item.active
    });
    setShowModal(true);
  };

  const getNextDisplayOrder = (category) => {
    const itemsInCategory = menuItems.filter(item => item.category === category);
    if (itemsInCategory.length === 0) return 1;
    return Math.max(...itemsInCategory.map(item => item.display_order)) + 1;
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.itemName.trim()) {
        setMessage({ type: 'warning', text: 'Item name is required' });
        return;
      }

      if (!formData.displayOrder || formData.displayOrder < 1) {
        setMessage({ type: 'warning', text: 'Display order must be a positive number' });
        return;
      }

      if (editingItem) {
        // Update existing item
        await adminAPI.updateMenuItem(editingItem.id, formData);
        setMessage({ type: 'success', text: 'Menu item updated successfully' });
      } else {
        // Create new item
        await adminAPI.createMenuItem(formData);
        setMessage({ type: 'success', text: 'Menu item created successfully' });
      }

      setShowModal(false);
      loadMenuItems();
    } catch (error) {
      console.error('Failed to save menu item:', error);
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.error || 'Failed to save menu item' 
      });
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.item_name}"?\n\nThis cannot be undone. If this item is used in orders or standing orders, it cannot be deleted.`)) {
      return;
    }

    try {
      await adminAPI.deleteMenuItem(item.id);
      setMessage({ type: 'success', text: 'Menu item deleted successfully' });
      loadMenuItems();
    } catch (error) {
      console.error('Failed to delete menu item:', error);
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.error || 'Failed to delete menu item' 
      });
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await adminAPI.updateMenuItem(item.id, { active: !item.active });
      setMessage({ 
        type: 'success', 
        text: `${item.item_name} is now ${!item.active ? 'active' : 'inactive'}` 
      });
      loadMenuItems();
    } catch (error) {
      console.error('Failed to toggle active status:', error);
      setMessage({ type: 'danger', text: 'Failed to update status' });
    }
  };

  // Group items by category
  const itemsByCategory = Object.fromEntries(
    MENU_CATEGORIES.map(cat => [cat, menuItems.filter(item => item.category === cat)])
  );

  return (
    <>
      <Card className="fade-in">
        <Card.Header>
          🍕 Menu Items Management
          <Button
            variant="light"
            size="sm"
            className="float-end"
            onClick={handleAddClick}
          >
            + Add Item
          </Button>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4">
            <strong>Manage your menu items:</strong> Add, edit, or remove items. Items can be marked 
            inactive instead of deleted to preserve order history.
          </Alert>

          {loading ? (
            <div className="text-center py-4">Loading menu items...</div>
          ) : (
            MENU_CATEGORIES.map(category => (
              <div key={category} className="mb-4">
                <h5 className="mb-3">
                  {category} Items 
                  <Badge bg="secondary" className="ms-2">
                    {itemsByCategory[category].length}
                  </Badge>
                </h5>
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>Order</th>
                      <th style={{ width: '20%' }}>Item Name</th>
                      <th style={{ width: '8%' }}>Item PLU</th>
                      <th style={{ width: '10%' }}>Mod PLU</th>
                      <th style={{ width: '27%' }}>Description</th>
                      <th style={{ width: '10%' }}>Status</th>
                      <th style={{ width: '20%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsByCategory[category].length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          No items in this category
                        </td>
                      </tr>
                    ) : (
                      itemsByCategory[category].map(item => (
                        <tr key={item.id} style={{ opacity: item.active ? 1 : 0.5 }}>
                          <td>{item.display_order}</td>
                          <td>
                            <strong>{item.item_name}</strong>
                          </td>
                          <td>
                            <small className="text-muted">{item.item_plu || '—'}</small>
                          </td>
                          <td>
                            <small className="text-muted">{item.modifier_plu || '—'}</small>
                          </td>
                          <td>
                            <small className="text-muted">
                              {item.description || <em>No description</em>}
                            </small>
                          </td>
                          <td>
                            {item.active ? (
                              <Badge bg="success">Active</Badge>
                            ) : (
                              <Badge bg="secondary">Inactive</Badge>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => handleEditClick(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant={item.active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              className="me-1"
                              onClick={() => handleToggleActive(item)}
                            >
                              {item.active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(item)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Category *</strong></Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setFormData({ 
                        ...formData, 
                        category: newCategory,
                        displayOrder: editingItem ? formData.displayOrder : getNextDisplayOrder(newCategory)
                      });
                    }}
                  >
                    {MENU_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Display Order *</strong></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                    placeholder="1, 2, 3..."
                  />
                  <Form.Text className="text-muted">
                    Order in which item appears in the list
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label><strong>Item Name *</strong></Form.Label>
              <Form.Control
                type="text"
                value={formData.itemName}
                onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                placeholder="e.g., Sausage Pizza, Soda, Napkins"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Item PLU</strong> (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.itemPlu}
                    onChange={(e) => setFormData({ ...formData, itemPlu: e.target.value })}
                    placeholder="e.g., 10042"
                  />
                  <Form.Text className="text-muted">
                    POS item lookup code
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Modifier PLU(s)</strong> (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.modifierPlu}
                    onChange={(e) => setFormData({ ...formData, modifierPlu: e.target.value })}
                    placeholder="e.g., 201, 305"
                  />
                  <Form.Text className="text-muted">
                    Comma-separated modifier codes
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label><strong>Description</strong> (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details about this item..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active (visible to customers)"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {editingItem ? 'Update Item' : 'Create Item'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default MenuItemsTab;
